import clsx from "clsx";
import React from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { Price, PriceProps } from "web/react/emo/price";
import { ProductImage } from "web/react/emo/product-image";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import * as styles from "./checkout-product-card.css";

export type ProductStatus =
    | "out-of-stock"
    | "item-cancelled"
    | "item-shipped"
    | "item-delivered"
    | "item-returned";

export interface CheckoutProductCardProps {
    imageSrc: string;
    imageAlt: string;
    title: string;
    price: PriceProps;
    size?: string;
    colour?: string;
    status?: ProductStatus;
    closeButtonAriaLabel?: string;
    onClose?: () => void;
    actionLabel?: string;
    actionOnClick?: () => void;
    className?: string;
    dataTestId?: string;
}

const STATUS_CONFIG: Record<
    ProductStatus,
    {
        label: string;
        icon: "error-filled" | "shipping-filled" | "delivered-filled" | "return-filled";
        variant: "error" | "success";
    }
> = {
    "out-of-stock": { label: "Out of Stock", icon: "error-filled", variant: "error" },
    "item-cancelled": { label: "Item Cancelled", icon: "error-filled", variant: "error" },
    "item-shipped": { label: "Item Shipped", icon: "shipping-filled", variant: "success" },
    "item-delivered": { label: "Item Delivered", icon: "delivered-filled", variant: "success" },
    "item-returned": { label: "Item Returned", icon: "return-filled", variant: "success" },
};

export function CheckoutProductCard({
    imageSrc,
    imageAlt,
    title,
    price,
    size,
    colour,
    status,
    closeButtonAriaLabel = "Remove item",
    onClose,
    actionLabel,
    actionOnClick,
    className,
    dataTestId,
}: CheckoutProductCardProps): React.ReactElement {
    return (
        <div
            className={clsx(styles.card, className)}
            data-testid={dataTestId || "checkout-product-card"}
        >
            <div className={styles.imageContainer}>
                <ProductImage src={imageSrc} alt={imageAlt} lazy={false} />
            </div>

            <div className={styles.content}>
                <VStack spacing="xxs">
                    {!!status && (
                        <HStack spacing="xxxs" align="center">
                            <SVGIcon
                                name={STATUS_CONFIG[status].icon}
                                className={clsx(
                                    styles.statusIcon,
                                    styles.statusVariants[STATUS_CONFIG[status].variant]
                                )}
                            />

                            <Text
                                textStyle="body-s"
                                className={styles.statusVariants[STATUS_CONFIG[status].variant]}
                            >
                                {STATUS_CONFIG[status].label}
                            </Text>
                        </HStack>
                    )}

                    <Text textStyle="body-s">{title}</Text>

                    <Price {...price} size="xs" />

                    <VStack spacing="xxxs">
                        {!!size && (
                            <Text textStyle="body-xs">
                                <Text as="span" color="secondary" textStyle="body-xs">
                                    {"Size: "}
                                </Text>
                                {size}
                            </Text>
                        )}

                        {!!colour && (
                            <Text textStyle="body-xs">
                                <Text as="span" color="secondary" textStyle="body-xs">
                                    {"Colour: "}
                                </Text>
                                {colour}
                            </Text>
                        )}
                    </VStack>

                    {!!actionLabel && actionOnClick && (
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={actionOnClick}
                        >
                            <Text textStyle="body-xs" underline>
                                {actionLabel}
                            </Text>
                        </button>
                    )}
                </VStack>
            </div>

            {!!onClose && (
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label={closeButtonAriaLabel}
                >
                    <SVGIcon name="clear-filled" className={styles.closeIcon} aria-hidden />
                </button>
            )}
        </div>
    );
}
